import React, { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

import { fmtMoney, fmtDateShort as fmtDate, weekStart, weekEnd } from '../../utils/formatters'
import { ARROW_L, ARROW_R, CHECK, WARN } from '../../utils/icons'

const CATEGORIES = [
  { value: 'fuel', label: 'FUEL', icon: String.fromCodePoint(0x26FD) },
  { value: 'materials', label: 'MATERIALS', icon: String.fromCodePoint(0x1F9F1) },
  { value: 'equipment', label: 'EQUIPMENT', icon: String.fromCodePoint(0x1F527) },
  { value: 'food', label: 'FOOD', icon: String.fromCodePoint(0x1F354) },
  { value: 'supplies', label: 'SUPPLIES', icon: String.fromCodePoint(0x1F4E6) },
  { value: 'mileage', label: 'MILEAGE', icon: String.fromCodePoint(0x1F697) },
  { value: 'trucking', label: 'TRUCKING', icon: String.fromCodePoint(0x1F69A) },
  { value: 'equipment_repair', label: 'EQUIPMENT REPAIR', icon: String.fromCodePoint(0x2699) },
  { value: 'truck_repair', label: 'TRUCK REPAIR', icon: String.fromCodePoint(0x1F6E0) },
  { value: 'subcontractor', label: 'SUBCONTRACTOR', icon: String.fromCodePoint(0x1F477) },
  { value: 'disposal', label: 'DISPOSAL/DUMP', icon: String.fromCodePoint(0x267B) },
  { value: 'rental', label: 'RENTAL', icon: String.fromCodePoint(0x1F3D7) },
  { value: 'labor', label: 'LABOR', icon: String.fromCodePoint(0x1F4AA) },
  { value: 'permits', label: 'PERMITS/FEES', icon: String.fromCodePoint(0x1F4C4) },
  { value: 'misc', label: 'MISC', icon: String.fromCodePoint(0x1F4CB) },
]

export default function ExpensePage() {
  const { user, profile, activeBusiness, isOwner, isOffice } = useAuth()
  const canManage = isOwner || isOffice
  const [tab, setTab] = useState('expenses')
  const [expenses, setExpenses] = useState([])
  const [allExpenses, setAllExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editExpense, setEditExpense] = useState(null)
  const [selectedWeek, setSelectedWeek] = useState(weekStart(new Date()))
  const [cardTransactions, setCardTransactions] = useState([])
  const [jobs, setJobs] = useState([])
  const [bankRows, setBankRows] = useState([])
  const [bankSaving, setBankSaving] = useState(false)
  const fileRef = useRef(null)
  const csvRef = useRef(null)
  const bankRef = useRef(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    const ws = selectedWeek.toISOString().split('T')[0]
    const we = weekEnd(selectedWeek).toISOString().split('T')[0]

    const { data: myExp } = await supabase.from('expenses')
      .select('*').eq('user_id', user.id)
      .gte('expense_date', ws).lte('expense_date', we)
      .order('expense_date', { ascending: false })
    setExpenses(myExp || [])

    if (canManage) {
      let q = supabase.from('expenses').select('*, profiles(full_name)')
        .gte('expense_date', ws).lte('expense_date', we)
        .order('expense_date', { ascending: false })
      if (activeBusiness !== 'all') q = q.eq('business_id', activeBusiness)
      const { data: all } = await q
      setAllExpenses(all || [])
    }

    const { data: jobData } = await supabase.from('jobs').select('id, job_number, client_name, business_id')
      .in('status', ['scheduled', 'active', 'in_progress']).order('job_number', { ascending: false })
    setJobs(jobData || [])

    setLoading(false)
  }, [user?.id, selectedWeek, activeBusiness, canManage])

  useEffect(() => { if (user) fetchData() }, [user, fetchData])

  const navigateWeek = (dir) => {
    const d = new Date(selectedWeek)
    d.setDate(d.getDate() + dir * 7)
    setSelectedWeek(weekStart(d))
  }

  const getWeekTotal = (exps) => exps.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)
  const getCategoryTotal = (exps, cat) => exps.filter(e => e.category === cat).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0)

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return
    await supabase.from('expenses').delete().eq('id', id)
    fetchData()
  }

  const handleApprove = async (id, status) => {
    await supabase.from('expenses').update({
      status,
      approved_by: user.id,
      approved_at: new Date().toISOString()
    }).eq('id', id)
    fetchData()
  }

  // Capital One CSV Import
  const handleCsvImport = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (evt) => {
      const text = evt.target.result
      const lines = text.split('\n').filter(l => l.trim())
      if (lines.length < 2) return
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase())
      const dateIdx = headers.findIndex(h => h.includes('date') && !h.includes('post'))
      const descIdx = headers.findIndex(h => h.includes('description') || h.includes('merchant'))
      const amtIdx = headers.findIndex(h => h.includes('debit') || h.includes('amount'))
      const catIdx = headers.findIndex(h => h.includes('category'))

      const txns = []
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g)
        if (!cols) continue
        const clean = cols.map(c => c.replace(/"/g, '').trim())
        const amt = parseFloat(clean[amtIdx])
        if (isNaN(amt) || amt <= 0) continue
        txns.push({
          date: clean[dateIdx] || '',
          description: clean[descIdx] || '',
          amount: amt,
          category: clean[catIdx] || '',
          matched: false,
          receiptId: null
        })
      }
      setCardTransactions(txns)
    }
    reader.readAsText(file)
  }

  // Match transactions to expenses by amount and date proximity
  const matchTransactions = () => {
    const matched = cardTransactions.map(txn => {
      const txnDate = new Date(txn.date)
      const match = allExpenses.find(exp => {
        const expDate = new Date(exp.expense_date)
        const dateDiff = Math.abs(txnDate - expDate) / 86400000
        const amtMatch = Math.abs(parseFloat(exp.amount) - txn.amount) < 0.02
        return amtMatch && dateDiff <= 3
      })
      return { ...txn, matched: !!match, receiptId: match?.id || null, receiptUrl: match?.receipt_url || null }
    })
    setCardTransactions(matched)
  }

  var handleBankImport = function(e) {
    var file = e.target.files[0]; if (!file) return
    var reader = new FileReader()
    reader.onload = function(evt) {
      var text = evt.target.result
      var lines = text.split('\n').filter(function(l) { return l.trim() })
      if (lines.length < 2) return
      var headers = lines[0].split(',').map(function(h) { return h.trim().replace(/"/g, '').toLowerCase() })
      var dateIdx = headers.findIndex(function(h) { return h.includes('date') })
      var descIdx = headers.findIndex(function(h) { return h.includes('description') || h.includes('merchant') || h.includes('memo') || h.includes('payee') })
      var amtIdx = headers.findIndex(function(h) { return h.includes('amount') || h.includes('debit') || h.includes('withdrawal') })
      if (dateIdx === -1) dateIdx = 0
      if (descIdx === -1) descIdx = 1
      if (amtIdx === -1) amtIdx = 2
      var rows = []
      for (var i = 1; i < lines.length; i++) {
        var cols = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g)
        if (!cols) continue
        var clean = cols.map(function(c) { return c.replace(/"/g, '').trim() })
        var rawAmt = parseFloat(clean[amtIdx])
        if (isNaN(rawAmt)) continue
        var amt = Math.abs(rawAmt)
        if (amt === 0) continue
        var rawDate = clean[dateIdx] || ''
        var isoDate = rawDate
        if (rawDate.includes('/')) { var parts = rawDate.split('/'); if (parts.length === 3) { isoDate = (parts[2].length === 2 ? '20' + parts[2] : parts[2]) + '-' + parts[0].padStart(2, '0') + '-' + parts[1].padStart(2, '0') } }
        rows.push({ date: isoDate, description: clean[descIdx] || '', amount: amt, category: 'misc', job_id: '', action: 'classify', business_id: activeBusiness !== 'all' ? activeBusiness : 'pools' })
      }
      setBankRows(rows)
    }
    reader.readAsText(file)
  }
  var updateBankRow = function(idx, field, value) { setBankRows(function(prev) { var copy = prev.map(function(r) { return Object.assign({}, r) }); copy[idx][field] = value; return copy }) }
  var saveBankExpenses = async function() {
    var toSave = bankRows.filter(function(r) { return r.action === 'classify' })
    if (toSave.length === 0) { alert('No expenses to save'); return }
    setBankSaving(true)
    var inserts = toSave.map(function(r) { return { user_id: user.id, business_id: r.business_id, job_id: r.job_id || null, category: r.category, amount: r.amount, description: r.description, vendor: r.description, expense_date: r.date, status: 'pending' } })
    var { error } = await supabase.from('expenses').insert(inserts)
    if (error) { alert('Error saving: ' + error.message) }
    else { alert(inserts.length + ' expenses saved!'); setBankRows([]); fetchData() }
    setBankSaving(false)
  }

  const myTotal = getWeekTotal(expenses)
  const allTotal = getWeekTotal(allExpenses)
  const unmatchedCount = cardTransactions.filter(t => !t.matched).length

  if (loading) return <div style={s.loading}>Loading...</div>

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <div style={s.title}>EXPENSES</div>
          <div style={s.subtitle}>{fmtDate(selectedWeek)} - {fmtDate(weekEnd(selectedWeek))}</div>
        </div>
        <div style={s.headerRight}>
          {canManage && (
            <div style={s.tabs}>
              {[['expenses','MY EXPENSES'],['review','REVIEW'],['import','IMPORT'],['card','CARD MATCH']].map(([k,l]) => (
                <button key={k} onClick={() => setTab(k)} style={{...s.tab,...(tab===k?s.tabActive:{})}}>{l}</button>
              ))}
            </div>
          )}
          <button onClick={() => { setEditExpense(null); setShowForm(true) }} style={s.addBtn}>+ NEW EXPENSE</button>
        </div>
      </div>

      <div style={s.weekNav}>
        <button onClick={() => navigateWeek(-1)} style={s.navBtn}>{ARROW_L}</button>
        <div style={s.weekLabel}>WEEK OF {selectedWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase()}</div>
        <button onClick={() => navigateWeek(1)} style={s.navBtn}>{ARROW_R}</button>
      </div>

      {/* My Expenses Tab */}
      {tab === 'expenses' && (
        <div>
          <div style={s.summaryBar}>
            <div style={s.summaryTotal}>
              <div style={s.summaryLabel}>WEEK TOTAL</div>
              <div style={s.summaryValue}>{fmtMoney(myTotal)}</div>
            </div>
            {CATEGORIES.filter(c => getCategoryTotal(expenses, c.value) > 0).map(c => (
              <div key={c.value} style={s.summaryCat}>
                <span>{c.icon}</span>
                <span style={s.summaryCatLabel}>{c.label}</span>
                <span style={s.summaryCatAmt}>{fmtMoney(getCategoryTotal(expenses, c.value))}</span>
              </div>
            ))}
          </div>

          <div style={s.section}>
            {expenses.length === 0 && <div style={s.empty}>No expenses this week.</div>}
            {expenses.map(exp => (
              <ExpenseRow key={exp.id} exp={exp} jobs={jobs} onEdit={() => { setEditExpense(exp); setShowForm(true) }} onDelete={() => handleDelete(exp.id)} canEdit={true} />
            ))}
          </div>
        </div>
      )}

      {/* Review Tab */}
      {tab === 'review' && canManage && (
        <div>
          <div style={s.summaryBar}>
            <div style={s.summaryTotal}>
              <div style={s.summaryLabel}>TEAM TOTAL</div>
              <div style={s.summaryValue}>{fmtMoney(allTotal)}</div>
            </div>
            <div style={s.summaryCat}>
              <span style={s.summaryCatLabel}>PENDING</span>
              <span style={{...s.summaryCatAmt, color: '#b48c3c'}}>{allExpenses.filter(e => e.status === 'pending').length}</span>
            </div>
            <div style={s.summaryCat}>
              <span style={s.summaryCatLabel}>APPROVED</span>
              <span style={{...s.summaryCatAmt, color: '#4a8c6a'}}>{allExpenses.filter(e => e.status === 'approved').length}</span>
            </div>
          </div>

          <div style={s.section}>
            {allExpenses.length === 0 && <div style={s.empty}>No team expenses this week.</div>}
            {allExpenses.map(exp => (
              <ExpenseRow key={exp.id} exp={exp} jobs={jobs} showUser={true} onApprove={(status) => handleApprove(exp.id, status)} canApprove={canManage} />
            ))}
          </div>
        </div>
      )}

      {/* Bank Statement Import Tab */}
      {tab === 'import' && canManage && (
        <div>
          <div style={s.cardHeader}>
            <div>
              <div style={s.sectionTitle}>IMPORT BANK STATEMENT</div>
              <div style={s.cardDesc}>Upload a CSV bank export. Classify each transaction and assign to jobs.</div>
            </div>
            <div style={s.cardActions}>
              <input ref={bankRef} type="file" accept=".csv" onChange={handleBankImport} style={{display:'none'}} />
              <button onClick={function() { bankRef.current?.click() }} style={s.importBtn}>UPLOAD CSV</button>
              {bankRows.length > 0 && <button onClick={saveBankExpenses} disabled={bankSaving} style={s.matchBtn}>{bankSaving ? 'SAVING...' : 'SAVE ALL (' + bankRows.filter(function(r){return r.action==='classify'}).length + ')'}</button>}
            </div>
          </div>
          {bankRows.length === 0 && (
            <div style={s.cardEmpty}>
              <div style={s.cardEmptyTitle}>NO TRANSACTIONS LOADED</div>
              <div style={s.cardEmptyDesc}>Download your bank statement as CSV and upload it above</div>
            </div>
          )}
          {bankRows.length > 0 && (
            <div>
              <div style={s.matchSummary}>
                <div style={s.matchStat}><div style={s.matchStatValue}>{bankRows.length}</div><div style={s.matchStatLabel}>TRANSACTIONS</div></div>
                <div style={s.matchStat}><div style={{...s.matchStatValue, color:'#4a8c6a'}}>{bankRows.filter(function(r){return r.action==='classify'}).length}</div><div style={s.matchStatLabel}>TO SAVE</div></div>
                <div style={s.matchStat}><div style={{...s.matchStatValue, color:'rgba(255,255,255,0.3)'}}>{bankRows.filter(function(r){return r.action==='skip'}).length}</div><div style={s.matchStatLabel}>SKIPPED</div></div>
                <div style={s.matchStat}><div style={{...s.matchStatValue, color:'#b48c3c'}}>{fmtMoney(bankRows.filter(function(r){return r.action==='classify'}).reduce(function(s,r){return s+r.amount},0))}</div><div style={s.matchStatLabel}>TOTAL</div></div>
              </div>
              {bankRows.map(function(row, idx) {
                return (
                  <div key={idx} style={{...s.txnRow, borderLeft: '3px solid ' + (row.action === 'skip' ? 'rgba(255,255,255,0.15)' : '#4a8c6a'), opacity: row.action === 'skip' ? 0.4 : 1}}>
                    <div style={{minWidth:'80px'}}><div style={s.txnDate}>{row.date}</div><div style={{...s.txnAmt, fontSize:'14px'}}>{fmtMoney(row.amount)}</div></div>
                    <div style={{flex:1,minWidth:'120px'}}><div style={{fontSize:'13px',color:'#ffffff'}}>{row.description}</div></div>
                    <select value={row.category} onChange={function(e){updateBankRow(idx,'category',e.target.value)}} style={{...s.formInput, width:'140px',padding:'6px 8px',fontSize:'12px'}} disabled={row.action==='skip'}>
                      {CATEGORIES.map(function(c){return <option key={c.value} value={c.value}>{c.label}</option>})}
                    </select>
                    <select value={row.job_id} onChange={function(e){updateBankRow(idx,'job_id',e.target.value)}} style={{...s.formInput, width:'160px',padding:'6px 8px',fontSize:'12px'}} disabled={row.action==='skip'}>
                      <option value="">No job</option>
                      {jobs.map(function(j){return <option key={j.id} value={j.id}>{j.job_number} - {j.client_name}</option>})}
                    </select>
                    <select value={row.business_id} onChange={function(e){updateBankRow(idx,'business_id',e.target.value)}} style={{...s.formInput, width:'90px',padding:'6px 8px',fontSize:'12px'}} disabled={row.action==='skip'}>
                      <option value="pools">CPP</option>
                      <option value="riverbend">RBC</option>
                    </select>
                    <button onClick={function(){updateBankRow(idx,'action',row.action==='skip'?'classify':'skip')}} style={{padding:'6px 10px',backgroundColor:row.action==='skip'?'rgba(74,140,106,0.15)':'rgba(255,255,255,0.05)',border:'1px solid '+(row.action==='skip'?'rgba(74,140,106,0.3)':'rgba(255,255,255,0.1)'),borderRadius:'3px',color:row.action==='skip'?'#4a8c6a':'rgba(255,255,255,0.4)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1px',cursor:'pointer',whiteSpace:'nowrap'}}>{row.action==='skip'?'UNDO':'SKIP'}</button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Card Match Tab */}
      {tab === 'card' && canManage && (
        <div>
          <div style={s.cardHeader}>
            <div>
              <div style={s.sectionTitle}>CAPITAL ONE CARD MATCHING</div>
              <div style={s.cardDesc}>Import your Capital One CSV statement to match transactions with receipts</div>
            </div>
            <div style={s.cardActions}>
              <input ref={csvRef} type="file" accept=".csv" onChange={handleCsvImport} style={{display:'none'}} />
              <button onClick={() => csvRef.current?.click()} style={s.importBtn}>IMPORT CSV</button>
              {cardTransactions.length > 0 && (
                <button onClick={matchTransactions} style={s.matchBtn}>RUN MATCHING</button>
              )}
            </div>
          </div>

          {cardTransactions.length === 0 && (
            <div style={s.cardEmpty}>
              <div style={s.cardEmptyIcon}>{'>'}</div>
              <div style={s.cardEmptyTitle}>NO TRANSACTIONS LOADED</div>
              <div style={s.cardEmptyDesc}>Download your Capital One statement as CSV and import it above</div>
            </div>
          )}

          {cardTransactions.length > 0 && (
            <div>
              <div style={s.matchSummary}>
                <div style={s.matchStat}>
                  <div style={s.matchStatValue}>{cardTransactions.length}</div>
                  <div style={s.matchStatLabel}>TRANSACTIONS</div>
                </div>
                <div style={s.matchStat}>
                  <div style={{...s.matchStatValue, color: '#4a8c6a'}}>{cardTransactions.filter(t => t.matched).length}</div>
                  <div style={s.matchStatLabel}>MATCHED</div>
                </div>
                <div style={s.matchStat}>
                  <div style={{...s.matchStatValue, color: unmatchedCount > 0 ? '#e05c5c' : 'rgba(255,255,255,0.3)'}}>{unmatchedCount}</div>
                  <div style={s.matchStatLabel}>NO RECEIPT</div>
                </div>
              </div>

              {cardTransactions.map((txn, i) => (
                <div key={i} style={{...s.txnRow, borderLeft: txn.matched ? '3px solid #4a8c6a' : '3px solid #e05c5c'}}>
                  <div style={s.txnDate}>{txn.date}</div>
                  <div style={s.txnDesc}>{txn.description}</div>
                  <div style={s.txnAmt}>{fmtMoney(txn.amount)}</div>
                  <div style={{...s.txnStatus, color: txn.matched ? '#4a8c6a' : '#e05c5c'}}>
                    {txn.matched ? CHECK + ' MATCHED' : WARN + ' NO RECEIPT'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Expense Form Modal */}
      {showForm && (
        <ExpenseForm
          expense={editExpense}
          jobs={jobs}
          user={user}
          activeBusiness={activeBusiness}
          profile={profile}
          fileRef={fileRef}
          onClose={() => { setShowForm(false); setEditExpense(null) }}
          onSave={() => { setShowForm(false); setEditExpense(null); fetchData() }}
        />
      )}
    </div>
  )
}

function ExpenseRow({ exp, onEdit, onDelete, onApprove, showUser, canEdit, canApprove, jobs }) {
  var [expanded, setExpanded] = useState(false)
  var cat = CATEGORIES.find(function(c) { return c.value === exp.category }) || CATEGORIES[6]
  var statusColors = { pending: '#b48c3c', approved: '#4a8c6a', rejected: '#e05c5c', reimbursed: '#4a6a8c' }
  var linkedJob = jobs && exp.job_id ? jobs.find(function(j) { return j.id === exp.job_id }) : null

  return (
    <div style={{...s.expRow, cursor: 'pointer'}} onClick={function() { setExpanded(!expanded) }}>
      <div style={s.expLeft}>
        <div style={s.expIcon}>{cat.icon}</div>
        <div>
          <div style={s.expDesc}>{exp.description || cat.label}</div>
          <div style={s.expMeta}>
            {fmtDate(exp.expense_date)}
            {exp.vendor && <span> - {exp.vendor}</span>}
            {showUser && exp.profiles?.full_name && <span> - {exp.profiles.full_name}</span>}
          </div>
        </div>
      </div>
      <div style={s.expRight}>
        <div style={s.expAmt}>{fmtMoney(exp.amount)}</div>
        <div style={{...s.expStatus, color: statusColors[exp.status] || '#b48c3c'}}>{exp.status?.toUpperCase()}</div>
        <div style={{...s.expBiz, color: exp.business_id === 'pools' ? '#b48c3c' : '#4a8c6a'}}>{exp.business_id === 'pools' ? 'POOLS' : 'RBC'}</div>
        {canEdit && <button onClick={function(e) { e.stopPropagation(); onEdit() }} style={s.editBtn}>EDIT</button>}
        {canEdit && <button onClick={function(e) { e.stopPropagation(); onDelete() }} style={s.deleteBtn}>X</button>}
        {canApprove && exp.status === 'pending' && (
          <div style={s.approveActions}>
            <button onClick={function(e) { e.stopPropagation(); onApprove('approved') }} style={s.approveBtn}>{CHECK}</button>
            <button onClick={function(e) { e.stopPropagation(); onApprove('rejected') }} style={s.rejectBtn}>X</button>
          </div>
        )}
      </div>
      {expanded && (
        <div style={{width:'100%',marginTop:'12px',paddingTop:'12px',borderTop:'1px solid rgba(255,255,255,0.06)',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 16px'}} onClick={function(e) { e.stopPropagation() }}>
          {exp.vendor && <div><div style={s.detailLabel}>VENDOR</div><div style={s.detailVal}>{exp.vendor}</div></div>}
          <div><div style={s.detailLabel}>CATEGORY</div><div style={s.detailVal}>{cat.icon} {cat.label}</div></div>
          {exp.description && <div style={{gridColumn:'1 / -1'}}><div style={s.detailLabel}>DESCRIPTION</div><div style={s.detailVal}>{exp.description}</div></div>}
          {exp.notes && <div style={{gridColumn:'1 / -1'}}><div style={s.detailLabel}>NOTES</div><div style={s.detailVal}>{exp.notes}</div></div>}
          {linkedJob && <div><div style={s.detailLabel}>LINKED JOB</div><div style={s.detailVal}>{linkedJob.job_number} - {linkedJob.client_name}</div></div>}
          <div><div style={s.detailLabel}>BUSINESS</div><div style={s.detailVal}>{exp.business_id === 'pools' ? 'CP Quality Pools' : 'Riverbend Clearing'}</div></div>
          {exp.receipt_url && <div style={{gridColumn:'1 / -1'}}><div style={s.detailLabel}>RECEIPT</div><img src={exp.receipt_url} alt="Receipt" style={{maxWidth:'100%',maxHeight:'200px',borderRadius:'4px',marginTop:'4px'}} /></div>}
        </div>
      )}
    </div>
  )
}

function ExpenseForm({ expense, jobs, user, activeBusiness, profile, onClose, onSave }) {
  const isEdit = !!expense
  const [form, setForm] = useState({
    category: expense?.category || 'fuel',
    amount: expense?.amount || '',
    description: expense?.description || '',
    vendor: expense?.vendor || '',
    expense_date: expense?.expense_date || new Date().toISOString().split('T')[0],
    job_id: expense?.job_id || '',
    notes: expense?.notes || '',
    business_id: expense?.business_id || (activeBusiness !== 'all' ? activeBusiness : profile?.business_ids?.[0] || 'pools'),
  })
  const [receiptFile, setReceiptFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [receiptPreview, setReceiptPreview] = useState(expense?.receipt_url || null)
  const fileInputRef = useRef(null)

  const handleFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setReceiptFile(file)
      setReceiptPreview(URL.createObjectURL(file))
    }
  }

  const update = (field, val) => setForm({ ...form, [field]: val })

  const handleSave = async () => {
    if (!form.amount || !form.category) return
    setSaving(true)

    let receiptUrl = expense?.receipt_url || null

    // Upload receipt if new file selected
    if (receiptFile) {
      const ext = receiptFile.name.split('.').pop()
      const path = user.id + '/' + Date.now() + '.' + ext
      const { error: uploadErr } = await supabase.storage.from('receipts').upload(path, receiptFile)
      if (!uploadErr) {
        const { data: urlData } = supabase.storage.from('receipts').getPublicUrl(path)
        receiptUrl = urlData.publicUrl
      }
    }

    const data = {
      user_id: user.id,
      business_id: form.business_id,
      job_id: form.job_id || null,
      category: form.category,
      amount: parseFloat(form.amount),
      description: form.description,
      vendor: form.vendor,
      expense_date: form.expense_date,
      receipt_url: receiptUrl,
      notes: form.notes,
      status: 'pending',
    }

    if (isEdit) {
      await supabase.from('expenses').update(data).eq('id', expense.id)
    } else {
      await supabase.from('expenses').insert(data)
    }

    setSaving(false)
    onSave()
  }

  return (
    <div style={s.modalOverlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>{isEdit ? 'EDIT EXPENSE' : 'NEW EXPENSE'}</div>
          <button onClick={onClose} style={s.modalClose}>X</button>
        </div>

        {/* Category selector */}
        <div style={s.catGrid}>
          {CATEGORIES.map(c => (
            <button key={c.value} onClick={() => update('category', c.value)}
              style={{...s.catBtn,...(form.category === c.value ? s.catBtnActive : {})}}>
              <span style={s.catBtnIcon}>{c.icon}</span>
              <span style={s.catBtnLabel}>{c.label}</span>
            </button>
          ))}
        </div>

        <div style={s.formGrid}>
          <div style={s.formField}>
            <label style={s.formLabel}>AMOUNT</label>
            <input type="number" value={form.amount} onChange={e => update('amount', e.target.value)}
              style={s.formInput} placeholder="0.00" step="0.01" min="0" />
          </div>
          <div style={s.formField}>
            <label style={s.formLabel}>DATE</label>
            <input type="date" value={form.expense_date} onChange={e => update('expense_date', e.target.value)} style={s.formInput} />
          </div>
          <div style={s.formField}>
            <label style={s.formLabel}>VENDOR</label>
            <input value={form.vendor} onChange={e => update('vendor', e.target.value)} style={s.formInput} placeholder="Store or vendor name" />
          </div>
          <div style={s.formField}>
            <label style={s.formLabel}>BUSINESS</label>
            <select value={form.business_id} onChange={e => update('business_id', e.target.value)} style={s.formInput}>
              <option value="pools">CP Quality Pools</option>
              <option value="riverbend">Riverbend Clearing</option>
            </select>
          </div>
          <div style={{...s.formField, gridColumn: '1 / -1'}}>
            <label style={s.formLabel}>DESCRIPTION</label>
            <input value={form.description} onChange={e => update('description', e.target.value)} style={s.formInput} placeholder="What was purchased" />
          </div>
          <div style={{...s.formField, gridColumn: '1 / -1'}}>
            <label style={s.formLabel}>ASSIGN TO JOB (OPTIONAL)</label>
            <select value={form.job_id} onChange={e => update('job_id', e.target.value)} style={s.formInput}>
              <option value="">No specific job</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.job_number} - {j.client_name}</option>)}
            </select>
          </div>
          <div style={{...s.formField, gridColumn: '1 / -1'}}>
            <label style={s.formLabel}>NOTES</label>
            <input value={form.notes} onChange={e => update('notes', e.target.value)} style={s.formInput} placeholder="Optional notes" />
          </div>
        </div>

        {/* Receipt Upload */}
        <div style={s.receiptUpload}>
          <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileChange} style={{display:'none'}} />
          <button onClick={() => fileInputRef.current?.click()} style={s.uploadBtn}>
            {receiptPreview ? 'CHANGE RECEIPT PHOTO' : 'UPLOAD RECEIPT PHOTO'}
          </button>
          {receiptPreview && (
            <div style={s.receiptThumb}>
              <img src={receiptPreview} alt="Receipt preview" style={s.receiptThumbImg} />
            </div>
          )}
        </div>

        <div style={s.modalActions}>
          <button onClick={onClose} style={s.cancelBtn}>CANCEL</button>
          <button onClick={handleSave} disabled={saving || !form.amount} style={s.saveBtn}>
            {saving ? 'SAVING...' : isEdit ? 'UPDATE EXPENSE' : 'SAVE EXPENSE'}
          </button>
        </div>
      </div>
    </div>
  )
}

const s = {
  container: { fontFamily: "'Barlow', sans-serif", maxWidth: '100%' },
  loading: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '15px' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' },
  title: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  headerRight: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  tabs: { display: 'flex', gap: '2px' },
  tab: { padding: '8px 16px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer', borderRadius: '3px' },
  tabActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  addBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '8px 16px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  weekNav: { display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'center', marginBottom: '20px' },
  weekLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.5)', minWidth: '140px', textAlign: 'center' },
  navBtn: { padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontSize: '16px', cursor: 'pointer' },
  summaryBar: { display: 'flex', alignItems: 'center', gap: '24px', padding: '20px 24px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '24px', flexWrap: 'wrap' },
  summaryTotal: {},
  summaryLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)' },
  summaryValue: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, color: '#ffffff' },
  summaryCat: { display: 'flex', alignItems: 'center', gap: '6px' },
  summaryCatLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.35)' },
  summaryCatAmt: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.6)' },
  section: { marginBottom: '24px' },
  sectionTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '14px', fontWeight: 600, letterSpacing: '2px', color: '#b48c3c', marginBottom: '16px' },
  empty: { padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.2)', fontSize: '14px' },
  expRow: { padding: '16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', marginBottom: '4px', flexWrap: 'wrap' },
  expLeft: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' },
  expIcon: { fontSize: '20px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '6px', flexShrink: 0 },
  expDesc: { fontSize: '15px', fontWeight: 600, color: '#ffffff' },
  expMeta: { fontSize: '12px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },
  expRight: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  expAmt: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '18px', fontWeight: 700, color: '#ffffff' },
  expStatus: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px' },
  expBiz: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px' },
  receiptBtn: { padding: '4px 8px', backgroundColor: 'rgba(74,106,140,0.15)', border: '1px solid rgba(74,106,140,0.3)', borderRadius: '3px', color: '#4a6a8c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  editBtn: { padding: '4px 8px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  deleteBtn: { padding: '4px 8px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', color: '#e05c5c', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
  approveActions: { display: 'flex', gap: '4px' },
  approveBtn: { padding: '6px 10px', backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', borderRadius: '3px', color: '#4a8c6a', fontSize: '14px', cursor: 'pointer' },
  rejectBtn: { padding: '6px 10px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', color: '#e05c5c', fontSize: '14px', cursor: 'pointer' },
  receiptPreview: { marginTop: '12px', padding: '12px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px' },
  receiptImg: { maxWidth: '100%', maxHeight: '300px', borderRadius: '4px' },
  // Card match
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' },
  cardDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  cardActions: { display: 'flex', gap: '8px' },
  importBtn: { padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '3px', color: 'rgba(255,255,255,0.6)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  matchBtn: { padding: '10px 20px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  cardEmpty: { textAlign: 'center', padding: '60px 24px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' },
  cardEmptyIcon: { fontSize: '32px', color: 'rgba(255,255,255,0.1)', marginBottom: '16px' },
  cardEmptyTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' },
  cardEmptyDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.2)' },
  matchSummary: { display: 'flex', justifyContent: 'space-around', padding: '20px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '16px' },
  matchStat: { textAlign: 'center' },
  matchStatValue: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '28px', fontWeight: 700, color: '#ffffff' },
  matchStatLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' },
  txnRow: { display: 'flex', alignItems: 'center', gap: '16px', padding: '14px 16px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '3px', marginBottom: '4px', flexWrap: 'wrap' },
  txnDate: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.5)', minWidth: '80px' },
  txnDesc: { flex: 1, fontSize: '14px', color: '#ffffff', minWidth: '150px' },
  txnAmt: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '16px', fontWeight: 700, color: '#ffffff' },
  txnStatus: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', minWidth: '100px' },
  // Modal
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '32px', width: '100%', maxWidth: '600px', maxHeight: '85vh', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  modalTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '2px', color: '#ffffff' },
  modalClose: { background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '18px', cursor: 'pointer' },
  catGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '6px', marginBottom: '24px' },
  catBtn: { padding: '12px 8px', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '4px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' },
  catBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)' },
  catBtnIcon: { display: 'block', fontSize: '20px', marginBottom: '4px' },
  catBtnLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', fontWeight: 600, letterSpacing: '1px', color: 'rgba(255,255,255,0.5)' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' },
  formField: {},
  formLabel: { display: 'block', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  formInput: { width: '100%', padding: '12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '14px', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  receiptUpload: { marginBottom: '20px' },
  uploadBtn: { width: '100%', padding: '14px', backgroundColor: 'transparent', border: '1px dashed rgba(255,255,255,0.15)', borderRadius: '4px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  receiptThumb: { marginTop: '12px' },
  receiptThumbImg: { maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' },
  modalActions: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  saveBtn: { padding: '10px 20px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed', sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  detailLabel: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: '9px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.25)', marginBottom: '3px' },
  detailVal: { fontSize: '13px', color: 'rgba(255,255,255,0.7)' },
}
